import {
  DocumentTemplate,
  Patient,
  PatientDocument,
  StructureUnit,
  Upload,
} from "@/src/data/allTypes";
import { useState } from "react";

import { getImgUrl, getUserId, jsonAbstract, uid } from "@/src/utils/utils";
import moment from "moment";

import apiFetch from "@/src/utils/apiFetch";
import { getUserShortName } from "@/src/utils/utils";

import { Button, CornerIconButton } from "@/src/components/buttons";
import { Card, CompartmentCard } from "@/src/components/cards";
import {
  closeDrawer,
  openConfirmDialog,
  openDrawer,
} from "@/src/components/dialogs";
import { FetchableList } from "@/src/components/fetchableLists";
import { Input } from "@/src/components/universalInput";
import { UploadCards, UploadSpot } from "@/src/components/uploadSpots";
import { USER } from "@/src/data/globalVariables";
import { getItemId } from "@/src/utils/utils";
import {
  IoDocumentTextOutline,
  IoPhoneLandscapeSharp,
  IoPhonePortraitSharp,
} from "react-icons/io5";
import { Tooltip } from "react-tooltip";
import { openDocumentForm, openEditor } from "../documents/documentsHook";
import { ConfidentialityCard } from "./confidentialitiesHook";
import { ExamsHistory } from "./examsHistory";

function MedecineLegaleDashboardUnits({ patient }: { patient: Patient }) {
  let units =
    USER?.get()?.service?.units?.filter(
      (unit) => unit?.type == "medecineLegale"
    ) || [];

  if (!units.length) return null;

  return (
    <div className="c3 gap15 ">
      {units.map((unit) => {
        if (!unit) return;
        if (unit.boxes?.length != 0) {
          let examReferences = [];
          for (let box of unit.boxes || []) {
            for (let type of box?.appointmentTypes || []) {
              if (!type?.exam) continue;
              examReferences.push(type.exam);
            }
          }
          if (examReferences?.length != 0) {
            return (
              <ExamsHistory
                key={unit?.id}
                title={unit?.name}
                patient={patient}
                references={examReferences}
              ></ExamsHistory>
            );
          }
        }

        return (
          <MedecineLegaleUnit
            key={unit?.id}
            patient={patient}
            unit={unit}
          ></MedecineLegaleUnit>
        );
      })}
    </div>
  );
}

function MedecineLegaleUnit({
  patient,
  unit,
}: {
  patient: Patient;
  unit: StructureUnit;
}) {
  const [reload, setReload] = useState(0);

  async function createNewMedecineLegaleDocument({
    patient,
    unit,
  }: {
    patient: Patient;
    unit: StructureUnit;
  }): Promise<PatientDocument | null> {
    let patientDocument: PatientDocument | null = null;
    await openDrawer({
      id: "documentTemplateChoiceDrawer",
      title: "Choisir un modèle de document",

      content: (
        <MedecineLegaleDocumentChoiceList
          unit={unit}
          onSelect={async (template) => {
            console.log("template", template);
            patientDocument = {
              patient: patient,
              creator: USER?.get(),
              template: template,
              parent: null,
              content: template.content,
              title: template.title || "Nouveau document",
              status: "pending",
              refAnswer: {
                creator: USER?.get(),
                reference: template.reference,
                patient: patient,
                fieldAnswers: [],
                notes: "",
                conclusion: "",
              },
            };

            await openDocumentForm({
              patient,
              patientDocument,
              onChange: (newValue) => {
                patientDocument = newValue;
              },
            });
          }}
        />
      ),
    });
    return patientDocument;
  }

  function getUploads(patientDocument: PatientDocument): Upload[] {
    let uploads: Upload[] = [];
    patientDocument.refAnswer?.fieldAnswers
      ?.filter((answer) => {
        return answer?.field?.type == "uploads";
      })
      .map((answer, index) => {
        let answerUploads = answer?.value as Upload[];
        if (!Array.isArray(answerUploads)) return;
        uploads = [...uploads, ...answerUploads];
      });

    patientDocument.refAnswer?.fieldAnswers
      ?.filter((answer) => {
        return answer?.field?.type == "upload";
      })
      .map((answer, index) => {
        let upload = answer?.value as Upload;
        if (!upload) return;
        uploads.push(upload);
      });

    console.log("patientDocument", patientDocument);

    return uploads;
  }

  return (
    <CompartmentCard
      key={unit.id}
      color="green"
      title={unit.name || ""}
      onClick={async () => {
        await createNewMedecineLegaleDocument({ patient, unit });
        setReload(reload + 1);
      }}
    >
      <FetchableList
        queryKey={["MedecineLegaleUnit", patient.id, unit.id, reload]}
        queryFn={() =>
          apiFetch(
            "patientDocuments",
            "loadMany",
            {
              templatesIds: unit.documentTemplates?.map(
                (documentTemplate) => documentTemplate?.id || ""
              ),
              patientId: patient.id || "",
            },
            "Erreur lors du chargement des documents"
          )
        }
      >
        {(data) => {
          return data?.map((patientDocument) => {
            return (
              <ConfidentialityCard key={uid()} item={patientDocument}>
                <Card
                  color={
                    patientDocument.status == "validated" ||
                    patientDocument.template?.isValidatable != true
                      ? "green"
                      : "simple"
                  }
                  title={
                    patientDocument.template?.name ||
                    patientDocument.title ||
                    ""
                  }
                  subTitle={getUserShortName(patientDocument.creator)}
                  onClick={async () => {
                    let fullDocument = await apiFetch(
                      "patientDocuments",
                      "loadOne",
                      {
                        id: patientDocument.id!,
                      },
                      "Erreur lors du chargement du document"
                    );

                    console.log("fullDocument", fullDocument);
                    openEditor({
                      patient,
                      patientDocument: fullDocument,
                    });
                  }}
                  onEdit={async () => {
                    let fullDocument = await apiFetch(
                      "patientDocuments",
                      "loadOne",
                      {
                        id: patientDocument.id!,
                      },
                      "Erreur lors du chargement du document"
                    );


                    await openDocumentForm({
                      patient,
                      patientDocument: fullDocument,
                    });

                    setReload(reload + 1);
                  }}
                  onDelete={async () => {
                    await openConfirmDialog({
                      id: "deleteUploadConfirm",
                      content: "Voulez-vous vraiment supprimer cet élément ?",
                      onConfirm: async () => {
                        await apiFetch(
                          "patientDocuments",
                          "deleteOne",
                          {
                            id: patientDocument.id,
                          },
                          "Erreur lors de la suppression du document"
                        );
                        setReload(reload + 1);
                      },
                    });
                  }}
                >
                  <div className="c1 gap10">
                    <div className="gray">
                      {moment(patientDocument._date).format("LL HH:mm ")}
                    </div>
                    <div className="c1 gap10">
                      {unit?.documentTemplates?.map((template, index) => {
                        if (!template) return null;
                        if (!getItemId(template.parent)) return null;
                        if (
                          getItemId(template.parent) !=
                          getItemId(patientDocument.template)
                        )
                          return null;
                        return (
                          <Button
                            key={uid()}
                            type={"tertiary"}
                            onClick={async () => {
                              let localPatientDocument: PatientDocument =
                                jsonAbstract(patientDocument);
                              localPatientDocument.template = template;
                              localPatientDocument.content = template.content;
                              localPatientDocument.title = template.title || "";

                              await openEditor({
                                patient,
                                patientDocument: localPatientDocument,
                                onChange: (newValue) => {},
                              });
                            }}
                          >
                            {template.name}
                          </Button>
                        );
                      })}
                    </div>
                    <div
                      className="c1 gap10"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <UploadCards
                        uploads={getUploads(patientDocument)}
                        count={3}
                      />
                    </div>
                  </div>
                </Card>
              </ConfidentialityCard>
            );
          });
        }}
      </FetchableList>
    </CompartmentCard>
  );
}

function MedecineLegaleDocumentChoiceList({
  unit,
  onSelect,
}: {
  unit: StructureUnit;
  onSelect: (newValue: DocumentTemplate) => void;
}) {
  return (
    <div className="grid c2 gap20">
      {unit?.documentTemplates?.map((template, index) => {
        if (template?.parent) return null;
        return (
          <div
            key={uid()}
            className="card center"
            onClick={async () => {
              onSelect(jsonAbstract(template) as DocumentTemplate);
            }}
          >
            <IoDocumentTextOutline size={20} />
            <div>{template?.name}</div>
          </div>
        );
      })}
    </div>
  );
}

function AutopsyAttachments({
  patient,
  current,
  onChange,
}: {
  patient: Patient;
  current?: Upload[];
  onChange: (newValue: Upload[]) => void;
}) {
  const [uploads, setUploads] = useState<Upload[]>(current || []);
  const [doubleUploads, setDoubleUploads] = useState<Upload[]>(current || []);
  return (
    <div className="c1 gap10">
      {
        <div className="grid c2 gap10">
          {uploads?.map((upload, index) => {
            let isDouble = doubleUploads.find(
              (doubleUpload) => doubleUpload.name === upload.name
            );
            return (
              <div
                key={uid()}
                className={`card ${isDouble ? "double" : ""}`}
                style={{ height: "400px" }}
                onClick={async () => {
                  await openDrawer({
                    title: "Modifier la pièce jointe",
                    content: (
                      <UploadForm
                        current={upload}
                        onChange={(newValue) => {
                          uploads[index] = newValue;
                          setUploads([...uploads]);
                          onChange([...uploads]);
                        }}
                      />
                    ),
                  });
                }}
              >
                <CornerIconButton
                  type="edit"
                  position="secondary"
                  onClick={() => {
                    if (isDouble) {
                      doubleUploads.splice(doubleUploads.indexOf(upload), 1);
                      setDoubleUploads([...doubleUploads]);
                    } else {
                      setDoubleUploads([...doubleUploads, upload]);
                    }
                  }}
                >
                  {isDouble ? (
                    <IoPhonePortraitSharp />
                  ) : (
                    <IoPhoneLandscapeSharp />
                  )}
                </CornerIconButton>
                <CornerIconButton
                  type="delete"
                  position="secondary"
                  onClick={async () => {
                    uploads.splice(index, 1);
                    setUploads([...uploads]);
                    onChange([...uploads]);
                  }}
                ></CornerIconButton>
                <div style={{ width: "100%", maxHeight: "300px" }}>
                  <img
                    src={getImgUrl(upload)}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
                <div className="gray p10">{upload.description}</div>
              </div>
            );
          })}
        </div>
      }
      <div className="p10">
        <Button
          type={"tertiary"}
          onClick={async () => {
            await openDrawer({
              id: "uploadsChoiceDrawer",
              title: "Choisir une pièce jointe",
              content: (
                <UploadsList
                  patient={patient}
                  onSelect={(upload) => {
                    setUploads([...uploads, upload]);
                    onChange([...uploads, upload]);
                    closeDrawer("uploadsChoiceDrawer");
                  }}
                ></UploadsList>
              ),
            });
          }}
        >
          Choisir une pièce jointe
        </Button>
      </div>
    </div>
  );
}

function UploadsList({
  patient,
  onSelect,
}: {
  patient?: Patient;
  onSelect: (newValue: Upload) => void;
}) {
  let [searchKey, setSearchKey] = useState("");
  const [reload, setReload] = useState(0);

  async function openItemDrawer(upload: Upload) {
    await openDrawer({
      title: "Ajouter un élément à l'historique",
      content: (
        <UploadForm
          patient={patient}
          current={upload}
          onChange={async (newValue) => {
            upload = newValue;
          }}
        />
      ),
      onConfirm: async () => {
        await apiFetch(
          "uploads",
          "addOne",
          { upload },
          "Erreur lors de l'ajout de la pièce jointe"
        );
        setReload((prev) => prev + 1);
      },
    });
  }

  return (
    <div>
      <FetchableList
        queryKey={["UploadsList", searchKey, reload]}
        queryFn={() =>
          apiFetch(
            "uploads",
            "loadMany",
            { patientId: patient?.id },
            "Erreur lors du chargement des pièces jointes"
          )
        }
        searchKey={searchKey}
        onSearch={(newValue) => {
          setSearchKey(newValue || "");
        }}
        onClick={async () => {
          let upload: Upload = {
            creator: getUserId(),
            fileType: "image",
            name: "",
            original_name: "",
            url: "",
            patient: patient,
          };
          await openItemDrawer(upload);
        }}
        emptyChild={
          searchKey && (
            <div className="center grid c1 gap10 p20">
              Aucune pièce jointe disponible
            </div>
          )
        }
      >
        {(data) => (
          <div className="grid c2 gap10">
            {data?.map((upload, index) => {
              return (
                <div
                  key={uid()}
                  className="card"
                  onClick={async () => {
                    onSelect(upload);
                  }}
                >
                  <CornerIconButton
                    type="delete"
                    position="secondary"
                    onClick={async () => {
                      await openConfirmDialog({
                        id: "deleteUploadConfirm",
                        content:
                          "Voulez-vous vraiment supprimer cette pièce jointe ?",
                        onConfirm: async () => {
                          await apiFetch(
                            "general",
                            "deleteAny",
                            {
                              id: upload.id,
                              table: "uploads",
                            },
                            "Erreur lors de la suppression de la pièce jointe"
                          );
                          setReload((prev) => prev + 1);
                        },
                      });
                    }}
                  ></CornerIconButton>
                  <CornerIconButton
                    type="edit"
                    onClick={async () => {
                      await openItemDrawer(upload);
                    }}
                  ></CornerIconButton>
                  <img
                    src={upload.url || ""}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "5px",
                    }}
                  />
                  <Tooltip
                    id={`tooltip${upload.id}`}
                    place="bottom"
                    variant="info"
                    content={upload.description || ""}
                    style={{ zIndex: 9999999999 }}
                  />
                  {/* <div className="bold ellipsis">{upload.name}</div> */}
                  <div
                    className="gray ellipsis"
                    data-tooltip-id={`tooltip${upload.id}`}
                  >
                    {upload.description}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </FetchableList>
    </div>
  );
}

function UploadForm({
  patient,
  current,
  onChange,
}: {
  patient?: Patient;
  current?: Upload;
  onChange: (newValue: Upload) => void;
}) {
  let [data, setData] = useState(current || ({} as Upload));
  function setNewValue(newValue: Upload) {
    setData(newValue);
    onChange(newValue);
  }

  return (
    <div className="grid c1 gap10">
      <UploadSpot
        value={data}
        additionalUploadData={{
          patient: getItemId(patient),
        }}
        onChange={(newValue) => {
          setNewValue({
            ...data,
            url: newValue.url || "",
            name: newValue.name,
          });
        }}
      ></UploadSpot>

      <Input
        name="Description"
        value={data.description}
        type="textarea"
        onChange={(newValue) => {
          setNewValue({ ...data, description: newValue });
        }}
      />
    </div>
  );
}

export {
  AutopsyAttachments,
  MedecineLegaleDashboardUnits,
  MedecineLegaleDocumentChoiceList,
  MedecineLegaleUnit,
};
